import { useLayoutEffect, useState } from 'react';

export const OPINION_EVENT_FLOATER_WIDTH = 460;
export const OPINION_EVENT_FLOATER_GAP = 6;
export const OPINION_EVENT_FLOATER_Z_INDEX = 9999999998;

export type TFloaterPlacement = 'below' | 'above';

export interface IOpinionEventFloaterAnchor {
  left: number;
  top: number;
  width?: number;
  height?: number;
}

function clampFloaterLeft(left: number, anchorWidth: number): number {
  let result = anchorWidth > 0 ? left : left - 30;
  if (result + OPINION_EVENT_FLOATER_WIDTH > window.innerWidth - 8) {
    result = window.innerWidth - OPINION_EVENT_FLOATER_WIDTH - 8;
  }
  return Math.max(result, 8);
}

function buildFloaterStyle(
  anchor: IOpinionEventFloaterAnchor,
  placement: TFloaterPlacement,
): React.CSSProperties {
  const anchorWidth = anchor.width ?? 0;
  const anchorHeight = anchor.height ?? 0;
  const left = clampFloaterLeft(anchor.left, anchorWidth);
  const baseStyle = {
    position: 'fixed' as const,
    left,
    width: OPINION_EVENT_FLOATER_WIDTH,
    zIndex: OPINION_EVENT_FLOATER_Z_INDEX,
  };

  if (placement === 'below') {
    return {
      ...baseStyle,
      top: anchor.top + anchorHeight + OPINION_EVENT_FLOATER_GAP,
    };
  }

  return {
    ...baseStyle,
    top: anchor.top - OPINION_EVENT_FLOATER_GAP,
    transform: 'translateY(-100%)',
  };
}

export function useOpinionEventFloaterPosition(
  visible: boolean,
  getAnchor: () => IOpinionEventFloaterAnchor | null,
  placement: TFloaterPlacement = 'below',
): React.CSSProperties | null {
  const [style, setStyle] = useState<React.CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!visible) {
      setStyle(null);
      return undefined;
    }

    const update = (): void => {
      const anchor = getAnchor();
      setStyle(anchor ? buildFloaterStyle(anchor, placement) : null);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [visible, getAnchor, placement]);

  return style;
}
